import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography, Container, Divider, Paper, Link, Stack } from '@mui/material';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';

import { Alert } from '../../../../../shared/lib/common/Alert';
import { useOptOutContactMutation, useValidateTokenContactLazyQuery } from '../../../generated';
import { images } from '../../../../../shared/lib/assets';
import { LINKS } from '../../constants';
import ContactOptOutConfirmation from '../../../components/ContactOptOutConfirmation';
import ContactFeature from '../../../components/common/ContactFeature';
import { contactOptInButtonStyle, promoButtonStyle, unbreakableTextStyle } from '../../../../../shared/lib/theme/styleConstants';
import {
  BUTTON_TEXT_DECLINE,
  COMPANY_NAME_RIGHTS,
  EMAIL_OPT_IN,
  EMAIL_OPT_IN_MAILTO,
  HEADING_DECLINE_TO_HELP,
  LABEL_SECURE_PONELESS_SERVICES,
  NOTE_OPT_OUT_BEFORE_OPTING_OUT,
  NOTE_OPT_OUT_EMAIL_PREFIX,
  NOTE_OPT_OUT_EMAIL_SUFFIX,
  NOTE_OPT_OUT_EXPLAIN_SERVICES,
  NOTE_OPT_OUT_MEMBER_CONTACT,
  NOTE_OPT_OUT_PREFIX,
  NOTE_OPT_OUT_SUGGEST_CONTACT,
  NOTE_OPT_OUT_SUFFIX
} from '../../../../../shared/lib/constants';

const OptOutContact = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const coupon = searchParams.get('coupon');
  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState<{ name: string; phoneNo: string } | null>(null);
  const [error, setError] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [removed, setRemoved] = useState(false);

  const [optOut, { loading: optOutLoading }] = useOptOutContactMutation({
    onCompleted: ({ optOutContact }) => {
      setRemoved(true);
      Alert.success(optOutContact?.message);
    },
    onError: ({ message }) => Alert.error(message)
  });

  const [validateToken, { loading: validateLoading }] = useValidateTokenContactLazyQuery({
    onCompleted: (data) => {
      const { name, phoneNo } = data?.validateTokenContact || {};
      if (!name || !phoneNo) {
        setError(true);
        return;
      }
      setUserInfo({ name, phoneNo });
    },
    onError: () => setError(true)
  });

  const validateContact = useCallback(() => {
    if (id) {
      validateToken({ variables: { optOutContactInput: { token: id } } });
    }
  }, [id, validateToken]);

  useEffect(() => {
    validateContact();
  }, [validateContact]);

  const loading = validateLoading || optOutLoading;

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      {/* Logo */}
      <Box display="flex" justifyContent="center" mb={4}>
        <Link href="https://855whodoicall.com" target="_blank" underline="none">
          <Box component="img" src={images.DASHBOARD_LOGO as unknown as string} sx={{ height: 60 }} />
        </Link>
      </Box>

      {/* Loader */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Paper
          elevation={4}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 5,
            background: 'linear-gradient(180deg, #F9FAFB 0%, #FFFFFF 100%)'
          }}
        >
          {error ? (
            <Typography variant="h5" color="error" align="center" fontWeight={600}>
              Invalid or Expired Link
            </Typography>
          ) : (
            <>
              {/* Header */}
              <Typography variant="h3" fontWeight={800} align="center" gutterBottom>
                Trusted Contact Information
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" align="center" mb={4}>
                {LABEL_SECURE_PONELESS_SERVICES} <strong>{COMPANY_NAME_RIGHTS}</strong>
              </Typography>

              {/* Inviter Info */}
              <Box
                sx={{
                  backgroundColor: '#F4F6F8',
                  borderRadius: 4,
                  p: 3,
                  textAlign: 'center',
                  mb: 4
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Invitation from
                </Typography>

                <Typography variant="h6" fontWeight={600}>
                  {userInfo?.name}
                </Typography>

                <Link href={`tel:${userInfo?.phoneNo}`} sx={{ fontSize: 16, textDecoration: 'none' }}>
                  {userInfo?.phoneNo}
                </Link>
              </Box>

              {/* Description */}
              <Typography variant="body1" mb={3} align="center">
                <strong>{userInfo?.name}</strong> added you as a <strong>VIP Trusted Contact</strong>. If they lose access to their phone, they can securely reach you from any phone.
              </Typography>

              {/* Features */}
              <Stack spacing={2} mb={4}>
                <ContactFeature icon={<SmsOutlinedIcon />} text="You’ll receive a text alert before the call" />
                <ContactFeature icon={<PhoneInTalkOutlinedIcon />} text="A toll-free call will be placed on their behalf" />
                <ContactFeature icon={<CheckCircleOutlineIcon />} text={`Caller ID will show ${userInfo?.name}’s name`} />
              </Stack>

              <Divider sx={{ my: 4 }} />

              {/* Coupon */}
              {coupon && (
                <Box sx={promoButtonStyle}>
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    🎁 Free Trial Offer
                  </Typography>

                  <Typography variant="body1" mb={2}>
                    Get <strong>3 months FREE</strong> as a thank you for being available for <strong>{userInfo?.name}</strong>.
                  </Typography>

                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      if (coupon) navigator.clipboard.writeText(coupon);
                      navigate(LINKS.REGISTER_LINK);
                    }}
                    sx={{ px: 4, py: 1.4, fontWeight: 700, textTransform: 'none' }}
                  >
                    Use Promo Code: {coupon}
                  </Button>
                </Box>
              )}

              {/* CTA */}
              {!removed ? (
                <Box textAlign="center">
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    {HEADING_DECLINE_TO_HELP(userInfo?.name || '')}
                  </Typography>

                  <Typography variant="body1" mb={3}>
                    If you do not wish to remain a Trusted Contact for <strong>{userInfo?.name}</strong>, you can decline below.
                  </Typography>

                  <Typography variant="body1" mb={3}>
                    {NOTE_OPT_OUT_PREFIX}
                    <strong>{NOTE_OPT_OUT_BEFORE_OPTING_OUT}</strong>
                    {NOTE_OPT_OUT_SUGGEST_CONTACT}
                    <strong>{NOTE_OPT_OUT_MEMBER_CONTACT(userInfo?.name || '', userInfo?.phoneNo || '')}</strong>
                    {NOTE_OPT_OUT_EXPLAIN_SERVICES}
                    {NOTE_OPT_OUT_EMAIL_PREFIX}
                    <Link href={EMAIL_OPT_IN_MAILTO} color="primary" underline="hover" sx={unbreakableTextStyle}>{EMAIL_OPT_IN}</Link>
                    {NOTE_OPT_OUT_EMAIL_SUFFIX}
                    <strong>{userInfo?.name}</strong>
                    {NOTE_OPT_OUT_SUFFIX}
                  </Typography>

                  <Button variant="contained" color="error" size="large" onClick={() => setConfirm(true)} sx={contactOptInButtonStyle}>
                    {BUTTON_TEXT_DECLINE}
                  </Button>
                </Box>
              ) : (
                <Box textAlign="center">
                  <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56, mb: 2 }} />
                  <Typography variant="h5" fontWeight={700} color="success.main">
                    You’ve been removed
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    You will no longer receive calls or notifications.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Paper>
      )}

      <ContactOptOutConfirmation
        username={userInfo?.name || ''}
        open={confirm}
        onClose={() => setConfirm(false)}
        onSubmit={() => {
          setConfirm(false);
          if (id) optOut({ variables: { optOutContactInput: { token: id } } });
        }}
      />
    </Container>
  );
};

export default OptOutContact;
